import { Entity } from '@inter-ikea-core/core';
import { Product, ProductDesign } from '@inter-ikea-storageone/range';
import ColorPicker from 'components/ProductSidebar/menus/Widgets/ChangeColor/ColorPicker';
import { useCore } from 'contexts/core';
import { getMarketProducts, getProductsForGroup, getSelectedGroupId } from 'ducks/products';
import { getFramesInScene } from 'rangeConfig/shared/utils/colorUtils/utils';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import Title from 'ui/Typography/Title';
import * as R from 'ramda';
import { getSelectedRange } from 'ducks/range';
import { showInfoMessage } from 'ducks/layout';
import { changeFrameColor } from 'rangeConfig/shared/utils/colorUtils/changeFrameColor';
import { getProductEntities } from 'ducks/core';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { useAppDispatch } from 'ducks/hooks';
import { getMajorityDesign } from 'rangeConfig/shared/utils/frontLayouts';
import { isMobile } from 'utils/platform';
import { useBehaviorSystem } from 'contexts/behaviors';
import { currentColor, setCurrentColor } from '../AutofillPax';

const ChangeFrameColor = () => {
  const { formatMessage } = useIntl();
  const productsInGroup = useSelector(getProductsForGroup);
  const coreApi = useCore();
  const dispatch = useAppDispatch();
  const productEntities = useSelector(getProductEntities);
  const products = useSelector(getMarketProducts);
  const range = useSelector(getSelectedRange);
  const [frameEntities, setFrameEntities] = useState<Entity[]>([]);
  const selectedCurrentColor = useSelector(currentColor);
  const behaviorSystem = useBehaviorSystem();
  const selectedGroupId = useSelector(getSelectedGroupId);

  useEffect(() => {
    const framesInScene = getFramesInScene(coreApi, range);
    const majorityDesign = getMajorityDesign(framesInScene);
    setFrameEntities(framesInScene);
    if (majorityDesign?.design) {
      dispatch(setCurrentColor(majorityDesign?.design as ProductDesign));
    }
  }, [coreApi, dispatch, productEntities, range]);

  const getProductDesigns = (products: Product[]) => {
    const designs = products
      .map(product => product.design.split('/')[0])
      .filter(design => design !== ProductDesign.DARK_GREY_METAL);
    return R.uniq(designs);
  };

  const designsInGroup = getProductDesigns(productsInGroup).filter(Boolean) as ProductDesign[];

  const checkNewSolution = (oldFrames: Entity[]): boolean => {
    const newFrames = getFramesInScene(coreApi, range);
    let nbrOfUnchangedFrames = 0;
    newFrames.forEach(frame => {
      if (oldFrames.includes(frame)) {
        nbrOfUnchangedFrames += 1;
      }
    });
    if (nbrOfUnchangedFrames === newFrames.length) {
      dispatch(showInfoMessage({ message: 'storageone.changeColorMissingFramesMessage' }));
      return true;
    }
    if (nbrOfUnchangedFrames > 0) {
      dispatch(showInfoMessage({ message: 'storageone.changeColorSomeMissingFramesMessage' }));
      return true;
    }
    return false;
  };

  const onChangeColor = (design: ProductDesign) => {
    behaviorSystem.startReport(`autofill-frame-color${design}`, {
      groupId: selectedGroupId,
      menuType: 'sheet',
      widgetId: 'autofill-frame-color',
      meta: { color: design }
    });

    const notReplaceableInteriorFound = changeFrameColor(coreApi, frameEntities, design, products);

    const unchangedFrames = checkNewSolution(frameEntities);
    if (!notReplaceableInteriorFound && unchangedFrames) {
      dispatch(showInfoMessage({ message: 'storageone.changeColorMissingFramesMessage' }));
    } else {
      dispatch(setCurrentColor(design));
    }
  };

  return designsInGroup.length > 1 ? (
    <>
      {
        <Title
          type={
            isMobile() ? ThemeFontStyleTypeEnum.headingXSmall : ThemeFontStyleTypeEnum.headingSmall
          }
          pb="xxs"
        >
          {formatMessage({ id: 'storageone.colorFilterLabel' })}
        </Title>
      }
      <ColorPicker
        colors={designsInGroup}
        currentColor={selectedCurrentColor}
        onChangeColor={onChangeColor}
      />
    </>
  ) : (
    <></>
  );
};

export default ChangeFrameColor;
